import React, { useEffect, useState, useContext, useRef, useMemo } from "react"
import { Grid, Container, Typography } from '@mui/material'
import { AuthContext } from "../../auth/Auth";
import { useInView } from 'react-intersection-observer';
import { CardTypes, ProjectSchemeInterface } from "ts-interfaces";
import { ProjectSchemeCreator } from "./ProjectSchemeCreator";
import { SearchBar } from "../components/SearchBar";
import { LayoutTabs } from "../components/LayoutTabs";
import { DisplayProjectSchemes } from "./DisplayProjectSchemes";
import { useProjectSchemas, useProjectSchemaDetails } from "../../global-state/useProjectSchemas";
import { ResponsiveGridLayout } from "../components/ResponsiveGridLayout";

export const ProjectScheme = ({ permissions, designFirms, teamMembers, setShowProjectManager, selectedProject, setSelectedProject }) => {
    const {currentUser} = useContext(AuthContext);
    const showArchived = window.location.search.indexOf('archive') > 0
    const [tab, setTab] = useState(0)
    const [filterString, setFilterString] = useState('');

    const { projects, setProjects } = useProjectSchemas((state) => ({
        projects: state.projects,
        setProjects: state.setProjects
    }));      

    // Calculate sharedProjectIds from permissions
    const sharedProjectIds = useMemo(() => {
        if (!permissions) return [];
        return [...new Set([
            ...(permissions.projectRead || []), 
            ...(permissions.projectWrite || [])
        ])];
    }, [permissions]);

    // Filter and categorize projects
    const { ownerProjects, sharedProjects } = useMemo(() => {
        if (!projects?.length || !currentUser) return { ownerProjects: [], sharedProjects: [] };
        
        return projects
            .filter(project => showArchived || !project.archive)
            .filter(project => !filterString || project.name?.toLowerCase().includes(filterString.toLowerCase()))
            .reduce<{ ownerProjects: ProjectSchemeInterface[], sharedProjects: ProjectSchemeInterface[] }>((result, project) => {
                if (project.owner === currentUser.uid) {
                    result.ownerProjects.push(project);
                } else if (sharedProjectIds.includes(project.id)) {
                    result.sharedProjects.push(project);
                }
                return result;
            }, { ownerProjects: [], sharedProjects: [] });
    }, [projects, currentUser, showArchived, filterString, sharedProjectIds]);

    const rootProjectContainer = useRef<HTMLDivElement>(null)
    const { ref, inView } = useInView({root:rootProjectContainer.current,threshold: 0,});
    const [showCreatorModal, setShowCreatorModal] = useState(false)
    const [projectThumbnails, setProjectThumbnails] = useState<Record<string, string[]>>({})
    
    const createNewProject = (project) => { setSelectedProject(project); setShowCreatorModal(true); }

    const handleProjectUpdate = (project: ProjectSchemeInterface, addProject = false) =>  {
        if (addProject) {
            useProjectSchemas.getState().addProject(project);

            // wait for state to update before syncing
            setTimeout(() => {
                useProjectSchemaDetails.getState().syncUserProjects();
            }, 0);
        } else {
            const updatedProjects = projects.map(p => p.id === project.id ? project : p);
            setProjects(updatedProjects);
            setTimeout(() => {
                useProjectSchemaDetails.getState().syncUserProjects();
            }, 0);
        }
    }
    const handleTabSwitch = (tabType: any) => void setTab(tabType);

    return (
    <Grid ref={rootProjectContainer}>
        {showCreatorModal && <ProjectSchemeCreator project={selectedProject} setShowCreatorModal={setShowCreatorModal} updateProject={handleProjectUpdate} />}
        <Container maxWidth={false}>
            <SearchBar  placeholder="Search by Project Name" setFilterString={setFilterString}/>
            <LayoutTabs tabType="My Projects" teamMembers={teamMembers} onTabSwitch={handleTabSwitch} />
            <Grid sx={{ marginTop: '20px' }} />
            <ResponsiveGridLayout 
                showNewCard={true}
                newCardProps={
                    tab === 0 ?
                    {
                        title: "New Project",
                        description: "Start a new project from scratch",
                        type: CardTypes.Projects,
                        onClick: () => createNewProject({})
                    } : undefined
                }
            >
                <DisplayProjectSchemes 
                    data={tab==0 ? ownerProjects : sharedProjects} 
                    setData={setProjects} 
                    designFirms={designFirms} 
                    teamMembers={teamMembers} 
                    selectedProject={selectedProject} 
                    setSelectedProject={setSelectedProject} 
                    setShowProjectManager={setShowProjectManager} 
                    projectThumbnails={projectThumbnails} 
                    setShowCreatorModal={setShowCreatorModal} 
                    onProjectUpdate={handleProjectUpdate}
                />
            </ResponsiveGridLayout>  
            {tab === 1 && sharedProjects.length === 0 && (
                <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '50vh',
                        textAlign: 'center',
                        color: '#555',
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                        No Shared Projects
                    </Typography>
                    <Typography variant="body1">
                        You currently have no projects shared with you. Please check back later or contact your team members.
                    </Typography>
                </Grid>
            )}           
        </Container>
        <Grid key={'placeholder'} ref={ref} item xs={6}>
            <img draggable={false} src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAHElEQVQI12P4//8/w38GIAXDIBKE0DHxgljNBAAO9TXL0Y4OHwAAAABJRU5ErkJggg==" height={1} alt="Red dot" />
        </Grid>
    </Grid>
    )
}